import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./SignIn.module.css";
import { API_BASE_URL, REDIRECT_AVEXA_BASE_URL } from "../../../config";
import { encryptPassword } from "../../../utils";
import AuthLeftPanel from "../../common/AuthLeftPanel";
import ComplianceBadges from "../../common/ComplianceBadge";
import { storePatientSession } from "../../../utils/storage/authStorage";
import { apiLoginByOtp, apiLoginByPassword, apiVerifyOtp } from "../../../api/authApi";

// ─────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────

function OTPInput({ length = 6, value = [], onChange }) {
  const refs = useRef([]);

  const handleChange = (e, idx) => {
    const digit = e.target.value.replace(/\D/, "").slice(-1);
    const next = [...value];
    next[idx] = digit;
    onChange(next);
    if (digit && idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !value[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (pasted) {
      onChange(pasted.split(""));
      refs.current[Math.min(pasted.length, length - 1)]?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className={styles.otpRow}>
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => (refs.current[idx] = el)}
          className={styles.otpBox}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[idx] || ""}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

function Notification({ type, message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      className={`${styles.notif} ${
        type === "success" ? styles.notifSuccess : styles.notifError
      }`}
    >
      {message}
    </div>
  );
}

function PasswordInput({ name, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className={styles.pwWrap}>
      <input
        className={styles.input}
        type={show ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="current-password"
      />
      <span className={styles.pwToggle} onClick={() => setShow((s) => !s)}>
        {show ? "Hide" : "Show"}
      </span>
    </div>
  );
}

function FormGroup({ label, error, children }) {
  return (
    <div className={styles.formGroup}>
      {label && <label className={styles.label}>{label}</label>}
      {children}
      {error && <span className={styles.errorMsg}>{error}</span>}
    </div>
  );
}

// ─────────────────────────────────────────
// Mobile bottom card (photoCard + compliance)
// Rendered below form on mobile only.
// On desktop these live inside AuthLeftPanel.
// ─────────────────────────────────────────

function MobileBottomCard() {
  return (
    <div className={styles.mobileBottomCard}>
      {/* Trust badge */}
      <div className={styles.mobileTrustBadge}>
        <div className={styles.mobileTrustIcon}>
          <img
            src="/images/tab4.svg"
            alt=""
            width={18}
            height={18}
            onError={(e) => { e.currentTarget.style.opacity = "0"; }}
          />
        </div>
        <div>
          <p className={styles.mobileTrustTitle}>Trusted by 2M+ patients</p>
          <p className={styles.mobileTrustSub}>across 1,000+ care centers</p>
        </div>
      </div>

      {/* Photo image */}
      <div className={styles.mobilePhotoWrap}>
        <img
          src="/images/family.svg"
          alt="Family using health app"
          className={styles.mobilePhotoImg}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>

      {/* Compliance badges */}
      <div className={styles.mobileCompliance}>
        <ComplianceBadges />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Validation
// ─────────────────────────────────────────

function validateEmail(values) {
  const errors = {};
  if (!values.email?.trim()) errors.email = "Email address is required";
  if (!values.password?.trim()) errors.password = "Password is required";
  return errors;
}

function validateMobile(values) {
  const errors = {};
  const mobile = String(values.mobile ?? "").replace(/\s/g, "").trim();
  if (!mobile) errors.mobile = "Mobile number is required";
  else if (!/^[0-9]{10}$/.test(mobile)) errors.mobile = "Enter a valid 10-digit number";
  return errors;
}



// ─────────────────────────────────────────
// SignIn page
// ─────────────────────────────────────────

export default function SignInPage() {
  const navigate = useNavigate();

  const [loginType, setLoginType] = useState("mobile");
  const [otpSent, setOtpSent] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [notif, setNotif] = useState(null);
  const [errors, setErrors] = useState({});

  const [values, setValues] = useState({
    email: "", password: "", mobile: "", otp: [],
  });

  const notify = useCallback((type, message) => setNotif({ type, message }), []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setOtpSent(false);
  }, []);

  const switchLoginType = (type) => {
    setLoginType(type);
    setValues({ email: "", password: "", mobile: "", otp: [] });
    setErrors({});
    setOtpSent(false);
  };

  // ── Email login ──
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const errs = validateEmail(values);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoginLoading(true);
    try {
      const response = await apiLoginByPassword({ email: values.email, password: values.password });
      storePatientSession(response);
      notify(
        "success",
        response?.message ||
          "Login successful!"
      );
      const secret = await encryptPassword(values.password);
      const redirectUrl = new URL(REDIRECT_AVEXA_BASE_URL);
      // redirectUrl.searchParams.set("email", values.email);
      // redirectUrl.searchParams.set("secret", secret);
      redirectUrl.searchParams.set("userId", response?.id);
      redirectUrl.searchParams.set("token", response?.accessToken);
      setTimeout(() => { window.location.href = redirectUrl.toString(); }, 1000);
    } catch (err) {
      notify("error", err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  // ── Request OTP ──
  const requestOTP = async () => {
    const errs = validateMobile(values);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setResendLoading(true);
    try {
      await apiLoginByOtp({ mobileNumber: values.mobile, orgName: "Mukta Women's Clinic" });
      notify("success", "OTP sent successfully");
      setOtpSent(true);
    } catch (err) {
      notify("error", err.message);
    } finally {
      setResendLoading(false);
    }
  };

  // ── Mobile login / OTP verify ──
  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    if (!otpSent) { await requestOTP(); return; }

    const otpStr = values.otp.join("");
    if (otpStr.length < 6) {
      setErrors((prev) => ({ ...prev, otp: "Please enter the full 6-digit OTP" }));
      return;
    }

    setLoginLoading(true);
    try {
      const response = await apiVerifyOtp(values.mobile, otpStr );
      storePatientSession(response);
      console.log("response ", response);
      const redirectUrl = new URL(REDIRECT_AVEXA_BASE_URL);
      redirectUrl.searchParams.set("userId", response?.id);
      redirectUrl.searchParams.set("token", response?.accessToken);
      notify("success", "Login successful!");
      setOtpSent(false);
      setTimeout(() => { window.location.href = redirectUrl.toString(); }, 1000);
    } catch (err) {
      notify("error", err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const isLoading = loginLoading || resendLoading;

  return (
    <div className={styles.page}>

      {/* Notification */}
      {notif && (
        <Notification
          type={notif.type}
          message={notif.message}
          onClose={() => setNotif(null)}
        />
      )}

      {/* Left panel */}
      <aside className={`ava-card-bg ${styles.leftPanel}`}>
        <AuthLeftPanel />
      </aside>

      {/* Right panel */}
      <main className={styles.rightPanel}>

        {/* Form card */}
        <div className={styles.formCard}>
          <h1 className={styles.formTitle}>Sign in</h1>

          {/* Toggle */}
          <div className={styles.toggle}>
            <button
              type="button"
              className={`${styles.toggleBtn} ${
                loginType === "mobile" ? styles.toggleBtnActive : ""
              }`}
              onClick={() => switchLoginType("mobile")}
            >
              Mobile Number
            </button>
            <button
              type="button"
              className={`${styles.toggleBtn} ${
                loginType === "email" ? styles.toggleBtnActive : ""
              }`}
              onClick={() => switchLoginType("email")}
            >
              Email Address
            </button>
          </div>

          {/* ── Email form ── */}
          {loginType === "email" && (
            <form  className={styles.formContent} onSubmit={handleEmailSubmit} noValidate>
              <FormGroup label="Email Address" error={errors.email}>
                <input
                  className={styles.input}
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  autoComplete="email"
                />
              </FormGroup>

              <FormGroup label="Password" error={errors.password}>
                <PasswordInput
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                />
              </FormGroup>

              <div className={styles.forgotRow}>
                <Link to="/forgot-password" className={styles.forgotLink}>
                  Forgot Password?
                </Link>
              </div>

              <button type="submit" className={` btn-primary`} disabled={isLoading}>
                {loginLoading ? "Signing in…" : "Submit"}
              </button>
            </form>
          )}

          {/* ── Mobile form ── */}
          {loginType === "mobile" && (
            <form  className={styles.formContent} onSubmit={handleMobileSubmit} noValidate>
              <FormGroup label="Mobile Number" error={errors.mobile}>
                <div className={styles.mobileRow}>
                  <span className={styles.countryCode}>+91</span>
                  <input
                    className={`${styles.input} ${styles.mobileInput}`}
                    type="tel"
                    name="mobile"
                    value={values.mobile}
                    onChange={handleChange}
                    maxLength={10}
                    placeholder="Enter mobile number"
                    autoComplete="tel"
                  />
                </div>
              </FormGroup>

              {otpSent && (
                <>
                  <FormGroup label="Enter OTP" error={errors.otp}>
                    <OTPInput
                      length={6}
                      value={values.otp}
                      onChange={(otp) => {
                        setValues((prev) => ({ ...prev, otp }));
                        setErrors((prev) => ({ ...prev, otp: undefined }));
                      }}
                    />
                  </FormGroup>

                  <div className={styles.resendRow}>
                    Didn't receive a code?{" "}
                    <span
                      className={styles.resendLink}
                      onClick={resendLoading ? undefined : requestOTP}
                    >
                      {resendLoading ? "Sending…" : "Resend Code"}
                    </span>
                  </div>
                </>
              )}

              <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                {isLoading ? "Please wait…" : "Submit"}
              </button>
            </form>
          )}
        </div>

        {/* ── Mobile-only: photoCard + compliance shown below form ── */}
        <MobileBottomCard />

      </main>
    </div>
  );
}